/**
 * Which landing page `/c/:audience` serves.
 *
 * Two designs now exist for the same route, so something has to choose between
 * them, and it cannot be `CampaignPage`: that component runs a long list of
 * hooks before it knows anything, and an early return past them breaks the
 * rules of hooks. The decision therefore sits one level up, at the route.
 *
 * The rule, and why:
 *
 *  - `newsroom` has NO legacy page. It is new in the Oct 2026 package and
 *    `audienceCopy.js` has no COPY entry for it, so the old component would
 *    throw on `copyFor()`. It always gets v3.
 *  - `journalist` and `marketing` keep their existing page by default and
 *    serve v3 on `?v=v3`. Those two URLs are live ad destinations; swapping
 *    what they serve is a decision for whoever is spending the money, not a
 *    side effect of building the new design. Flipping the default later is
 *    one line here.
 *  - `publisher` has no v3 design in the package and is untouched.
 */

import React from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

import CampaignPage from '../CampaignPage';
import LandingPageV3 from './LandingPageV3';
import { landingConfigFor } from './landingConfig';
import { resolveVariant } from '../campaignVariant';

export default function CampaignRoute() {
  const { audience } = useParams();
  const { search } = useLocation();
  const navigate = useNavigate();

  const v3 = landingConfigFor(audience);
  // Audiences the OLD page can render. Kept as a literal rather than imported
  // so that adding a v3 config can never accidentally widen the legacy set.
  const hasLegacyPage = ['journalist', 'publisher', 'marketing'].includes(audience);

  if (v3 && (!hasLegacyPage || resolveVariant(search) === 'v3')) {
    // Same handoff as `/lp/`. This path serves the identical page, so a trial
    // signup must reach the identical confirmation: two post-submit screens for
    // one page would differ only by which URL the ad happened to use.
    return (
      <LandingPageV3
        config={v3}
        onTrialSubmitted={() => navigate('/trial/confirmation')}
      />
    );
  }
  return <CampaignPage />;
}
